/**
 * Reusable layout configurations for PixiJS Layout v3
 * Uses Yoga-powered flexbox for consistent layouts
 */

import type { LayoutStyles } from '@pixi/layout';
import { SPACING } from './theme';

/**
 * Common layout configurations
 */
export const LAYOUT_STYLES = {
  /**
   * Center content both horizontally and vertically
   */
  centered: {
    justifyContent: 'center',
    alignItems: 'center',
  } satisfies Partial<LayoutStyles>,

  /**
   * Horizontal row layout with optional gap
   */
  row: (gap: number = SPACING.MD): Partial<LayoutStyles> => ({
    flexDirection: 'row',
    gap,
  }),

  /**
   * Vertical column layout with optional gap
   */
  column: (gap: number = SPACING.SM): Partial<LayoutStyles> => ({
    flexDirection: 'column',
    gap,
  }),

  /**
   * Full-size container that fills given dimensions
   */
  fullSize: (width: number, height: number): Partial<LayoutStyles> => ({
    width,
    height,
  }),

  /**
   * Centered full-screen layout
   */
  centeredScreen: (width: number, height: number): LayoutStyles => ({
    width,
    height,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: SPACING.LG,
  }),

  /**
   * Card internal layout (vertical stack)
   */
  cardLayout: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: SPACING.XS,
  } satisfies Partial<LayoutStyles>,

  /**
   * Wrapped gallery layout (for warrior selection grids)
   */
  gallery: (width: number, gap: number = SPACING.SM): LayoutStyles => ({
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap,
    justifyContent: 'center',
    width,
  }),

  /**
   * Three-column game layout (landscape mode)
   */
  threeColumnRow: (width: number, height: number, gap: number = SPACING.MD): LayoutStyles => ({
    width,
    height,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap,
  }),

  /**
   * Three-row game layout (portrait mode)
   */
  threeRowColumn: (width: number, height: number, gap: number = SPACING.MD): LayoutStyles => ({
    width,
    height,
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap,
  }),
} as const;
